/** @format */
import { useQuery } from '@tanstack/react-query';
import { useContext, useEffect } from 'react';

import { getProductSearchResultsQueryKey } from '@toucan/common/services/search/product/query-keys';
import { fetchProductSearchResults } from '@toucan/common/services/search/product/requests';

import { Milliseconds } from '@toucan/client/global/application';
import { useFeatureFlag } from '@toucan/client/global/feature-flag';
import { MarketContext } from '@toucan/client/global/market';

import { SearchResultsVariant } from '@toucan/client/features/search/components/search-results';
import {
    addTagsToListingResults,
    validateBaseQuery,
} from '@toucan/client/features/search/helpers';
import { addSearchResultsToDataLayer } from '@toucan/client/features/search/helpers/analytics';

import { NULL_SEARCH_RESULTS } from './constants';
import {
    UseProductSearchResultsProps,
    UseProductSearchResultsReturn,
} from './types';

export const useProductSearchResults = ({
    baseQuery,
    searchParams,
    resultsPerPage,
    sortedBy,
    modifierGroups,
}: UseProductSearchResultsProps): UseProductSearchResultsReturn => {
    const { activeMarket } = useContext(MarketContext);
    const { pageIndex, filterQuery, sort } = searchParams;

    const shouldFetchTags =
        useFeatureFlag('BEAK-3676-(usp-tags)')?.isEnabled ?? false;

    const isWaitingForModifierGroups =
        Array.isArray(filterQuery.productPriceModifierIds) && !modifierGroups;

    const { data, isFetching, dataUpdatedAt } = useQuery({
        enabled: validateBaseQuery(baseQuery) && !isWaitingForModifierGroups,
        queryKey: getProductSearchResultsQueryKey(
            baseQuery,
            searchParams,
            resultsPerPage
        ),
        queryFn: async () => {
            const response = await fetchProductSearchResults(
                baseQuery,
                searchParams,
                resultsPerPage,
                activeMarket,
                modifierGroups
            );

            const { facets, ...results } = await addTagsToListingResults(
                response,
                activeMarket.code,
                shouldFetchTags
            );

            return results;
        },
        select: (results) => ({
            ...results,
            sort,
            results: results.results.map((result) => ({
                ...result,
                tags: result.tags ?? { values: [] },
            })),
        }),
        keepPreviousData: pageIndex > 0,
        refetchOnWindowFocus: false,
        staleTime: Milliseconds.FIVE_MINUTES,
    });

    const searchResults = data ?? NULL_SEARCH_RESULTS;

    useEffect(() => {
        if (!data) return;

        addSearchResultsToDataLayer({
            searchResults: data.results.slice(pageIndex * resultsPerPage),
            pageType: 'model',
            searchVariant: SearchResultsVariant.PRODUCT,
            searchFilters: { ...baseQuery.filterQuery, ...filterQuery },
            sortedBy,
            page: pageIndex + 1,
        });
    }, [dataUpdatedAt]);

    return {
        searchResults,
        numResultSkeletons: data && !isFetching ? 0 : resultsPerPage,
    };
};
